import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Banknote, RefreshCw } from 'lucide-react';

import { bankDepositApi } from '@/api/bankDeposit.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { Button, Combobox, Input, Spinner, Toggle } from '@/components/ui';
import type { ComboboxOption } from '@/components/ui/Combobox';
import { BankAccountLookup } from '@/components/lookups/EntityLookups';
import { costCentresForLocation } from '@/utils/costCentreScope';
import { cn } from '@/utils/cn';
import type { RootState } from '@/state/store';
import type {
  CashAccountOptionDto,
  CashDepositResultDto,
  CashToDepositRowDto,
  UnbankedCashAgeRowDto,
} from '@/types/bankDeposit.types';

/**
 * Cash Deposit: bank the cash posted to one cash in hand account, cost centre by cost centre and
 * day by day. Each row shows what was posted (net), what is already deposited, what is waiting in
 * a saved deposit, and what is left. Typing an amount on a row and saving makes one Bank Deposit;
 * a user allowed to confirm deposits posts it in the same step.
 */

type Tab = 'deposit' | 'ageing';
type DateMode = 'asOf' | 'range';
const ALL = '__all__';

const today = () => new Date().toISOString().split('T')[0];
const money = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dayLabel = (iso: string) => {
  const [y, m, d] = iso.split('T')[0].split('-');
  return `${Number(d)} ${MONTHS[Number(m) - 1]} ${y}`;
};
const rowKey = (r: Pick<CashToDepositRowDto, 'costCenterId' | 'businessDate'>) =>
  `${r.costCenterId}|${r.businessDate.split('T')[0]}`;
const errorText = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback;

const labelCls = 'block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1';
const thCls = 'px-3 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap';
const tdCls = 'px-3 py-2 text-sm text-slate-800 dark:text-slate-100 whitespace-nowrap';

const Required = () => <span className="text-red-500" aria-hidden> *</span>;

export const CashDepositPage = () => {
  const { hasPermission } = usePermission();
  const canCreate  = hasPermission(Permissions.CASH_DEPOSIT_CREATE);
  const canConfirm = hasPermission(Permissions.CASH_DEPOSIT_CONFIRM);

  const session    = useSelector((s: RootState) => s.session.session);
  const accessible = useSelector((s: RootState) => s.auth.accessibleCostCenters);
  const costCentres = useMemo(() => costCentresForLocation(accessible, null), [accessible]);
  const ccLabel = useCallback((id: string) => {
    const cc = costCentres.find(c => c.id === id);
    return cc ? `${cc.name} (${cc.code})` : '';
  }, [costCentres]);

  // ── Header ────────────────────────────────────────────────────────────────
  const [depositDate, setDepositDate]     = useState(today());
  const [cashAccountId, setCashAccountId] = useState('');
  const [costCenterId, setCostCenterId]   = useState('');
  const [bankAccountId, setBankAccountId] = useState('');
  const [slipNo, setSlipNo]               = useState('');
  const [remarks, setRemarks]             = useState('');

  // ── Filters ───────────────────────────────────────────────────────────────
  const [filterCc, setFilterCc]   = useState('');           // '' follows the header's cost centre
  const [dateMode, setDateMode]   = useState<DateMode>('asOf');
  const [asOf, setAsOf]           = useState(today());
  const [dateFrom, setDateFrom]   = useState('');
  const [dateTo, setDateTo]       = useState(today());
  const [showDeposited, setShowDeposited] = useState(false);

  const [tab, setTab] = useState<Tab>('deposit');

  // ── Data ──────────────────────────────────────────────────────────────────
  const [cashAccounts, setCashAccounts] = useState<CashAccountOptionDto[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [rows, setRows]         = useState<CashToDepositRowDto[]>([]);
  const [ageing, setAgeing]     = useState<UnbankedCashAgeRowDto[]>([]);
  const [loading, setLoading]   = useState(false);
  const [amounts, setAmounts]   = useState<Record<string, string>>({});
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [saved, setSaved]       = useState<CashDepositResultDto | null>(null);

  useEffect(() => {
    bankDepositApi.getCashAccounts()
      .then(r => setCashAccounts(r.data?.data ?? []))
      .catch(() => setCashAccounts([]))
      .finally(() => setAccountsLoading(false));
  }, []);

  // Land on the session's cost centre when it is one the user may use.
  useEffect(() => {
    if (costCenterId || costCentres.length === 0) return;
    const fromSession = costCentres.find(c => c.id === session?.costCenterId);
    setCostCenterId(fromSession?.id ?? (costCentres.length === 1 ? costCentres[0].id : ''));
  }, [costCentres, session?.costCenterId, costCenterId]);

  // Landing on a cost centre suggests the cash account its POS cash is mapped to.
  useEffect(() => {
    if (!costCenterId || cashAccountId) return;
    const mapped = cashAccounts.find(a => a.costCenterIds.includes(costCenterId));
    if (mapped) setCashAccountId(mapped.id);
  }, [costCenterId, cashAccounts, cashAccountId]);

  // Changing the cost centre moves off a cash account that belongs to another cost centre.
  const changeCostCentre = (v: string) => {
    setCostCenterId(v);
    setAmounts({});
    const current = cashAccounts.find(a => a.id === cashAccountId);
    if (current && (current.costCenterIds.length === 0 || current.costCenterIds.includes(v))) return;
    const mapped = cashAccounts.find(a => a.costCenterIds.includes(v));
    if (mapped) setCashAccountId(mapped.id);
  };

  const accountOptions: ComboboxOption[] = useMemo(
    () => cashAccounts.map(a => ({ value: a.id, label: `${a.code} ${a.name}` })), [cashAccounts]);
  const ccOptions: ComboboxOption[] = useMemo(
    () => costCentres.map(c => ({ value: c.id, label: `${c.name} (${c.code})` })), [costCentres]);
  const filterOptions: ComboboxOption[] = useMemo(
    () => [{ value: ALL, label: 'All my cost centres' }, ...ccOptions], [ccOptions]);

  const effectiveFilterCc = filterCc === '' ? costCenterId : filterCc;
  const filterCcParam = effectiveFilterCc && effectiveFilterCc !== ALL ? effectiveFilterCc : undefined;

  // ── Load ──────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!cashAccountId) { setRows([]); setAgeing([]); return; }
    if (dateMode === 'range' && dateFrom && dateTo && dateFrom > dateTo) {
      setError('The start date must be on or before the end date.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      if (tab === 'deposit') {
        const res = await bankDepositApi.getCashToDeposit({
          cashAccountId,
          costCenterId: filterCcParam,
          ...(dateMode === 'asOf'
            ? { asOf: asOf || undefined }
            : { dateFrom: dateFrom || undefined, dateTo: dateTo || undefined }),
          includeDeposited: showDeposited,
        });
        setRows(res.data?.data ?? []);
      } else {
        const res = await bankDepositApi.getUnbankedCashAgeing({
          cashAccountId, costCenterId: filterCcParam, asOf: asOf || undefined,
        });
        setAgeing(res.data?.data ?? []);
      }
    } catch (err) {
      setError(errorText(err, 'Could not load the cash for this account.'));
    } finally {
      setLoading(false);
    }
  }, [cashAccountId, filterCcParam, dateMode, asOf, dateFrom, dateTo, showDeposited, tab]);

  useEffect(() => { load(); }, [load]);

  // ── Entered amounts ───────────────────────────────────────────────────────
  const amountOf = (r: CashToDepositRowDto) => {
    const v = parseFloat(amounts[rowKey(r)] ?? '');
    return Number.isFinite(v) ? v : 0;
  };
  const rowError = (r: CashToDepositRowDto): string | null => {
    const raw = amounts[rowKey(r)];
    if (!raw) return null;
    const v = parseFloat(raw);
    if (!Number.isFinite(v)) return 'Enter a number.';
    if (v < 0) return 'Cannot be negative.';
    if (Math.round(v * 100) > Math.round(r.undepositedAmount * 100))
      return `More than the ${money(r.undepositedAmount)} not yet deposited.`;
    return null;
  };

  const showPending = rows.some(r => r.pendingAmount !== 0);
  const enteredRows = rows.filter(r => amountOf(r) > 0);
  const anyRowError = rows.some(r => rowError(r) !== null);
  const totals = rows.reduce((t, r) => ({
    net: t.net + r.netAmount,
    deposited: t.deposited + r.depositedAmount,
    pending: t.pending + r.pendingAmount,
    undeposited: t.undeposited + r.undepositedAmount,
    entered: t.entered + amountOf(r),
  }), { net: 0, deposited: 0, pending: 0, undeposited: 0, entered: 0 });
  const currency = rows[0]?.currencyCode ?? ageing[0]?.currencyCode ?? session?.companyCurrency ?? '';

  const missing: string[] = [];
  if (!depositDate)   missing.push('deposit date');
  if (!cashAccountId) missing.push('cash in hand account');
  if (!costCenterId)  missing.push('cost centre');
  if (!bankAccountId) missing.push('bank account');

  const canSave = canCreate && !saving && missing.length === 0 && enteredRows.length > 0 && !anyRowError;
  const saveLabel = canConfirm ? 'Save and post deposit' : 'Save deposit for approval';

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    setSaved(null);
    try {
      const res = await bankDepositApi.createCashDeposit({
        depositDate,
        cashAccountId,
        costCenterId,
        bankAccountId,
        depositSlipNo: slipNo.trim() || null,
        remarks: remarks.trim() || null,
        lines: enteredRows.map(r => ({
          costCenterId: r.costCenterId,
          businessDate: r.businessDate.split('T')[0],
          amount: Math.round(amountOf(r) * 100) / 100,
        })),
      });
      setSaved(res.data?.data ?? null);
      setAmounts({});
      setSlipNo('');
      setRemarks('');
      await load();
    } catch (err) {
      setError(errorText(err, 'The deposit could not be saved.'));
    } finally {
      setSaving(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900 overflow-auto">
      {/* Title */}
      <div className="flex-shrink-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 py-3 flex items-center gap-3">
        <Banknote size={18} className="text-teal-600" />
        <div>
          <h1 className="text-base font-semibold text-slate-800 dark:text-slate-100">Cash Deposit</h1>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Bank the cash in hand, cost centre by cost centre and day by day.
          </p>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Header */}
        <section className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={labelCls} htmlFor="cd-date">Deposit date<Required /></label>
              <Input id="cd-date" type="date" value={depositDate} onChange={e => setDepositDate(e.target.value)} />
            </div>
            <div>
              <span className={labelCls}>Cash in hand account<Required /></span>
              <Combobox
                options={accountOptions}
                value={cashAccountId}
                onChange={v => { setCashAccountId(v); setAmounts({}); setSaved(null); }}
                placeholder={accountsLoading ? 'Loading...' : accountOptions.length === 0 ? 'No cash in hand accounts set up' : 'Search cash accounts'}
                disabled={accountsLoading}
                clearable={false}
              />
            </div>
            <div>
              <span className={labelCls}>Cost centre<Required /></span>
              <Combobox
                options={ccOptions}
                value={costCenterId}
                onChange={changeCostCentre}
                placeholder={ccOptions.length === 0 ? 'No cost centres available to you' : 'Search cost centres'}
                clearable={false}
              />
            </div>
            <div>
              <span className={labelCls}>Bank account to deposit into<Required /></span>
              <BankAccountLookup value={bankAccountId} onChange={v => setBankAccountId(v ?? '')}
                                 placeholder="Search bank accounts" />
            </div>
            <div>
              <label className={labelCls} htmlFor="cd-slip">Deposit slip number</label>
              <Input id="cd-slip" value={slipNo} maxLength={200} onChange={e => setSlipNo(e.target.value)}
                     placeholder="Optional" />
            </div>
            <div>
              <label className={labelCls} htmlFor="cd-remarks">Remarks</label>
              <Input id="cd-remarks" value={remarks} maxLength={1000} onChange={e => setRemarks(e.target.value)}
                     placeholder="Optional" />
            </div>
          </div>
        </section>

        {/* Filters */}
        <section className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="w-64">
              <span className={labelCls}>Show cost centre</span>
              <Combobox
                options={filterOptions}
                value={effectiveFilterCc || ALL}
                onChange={v => setFilterCc(v || ALL)}
                clearable={false}
              />
            </div>
            <div>
              <span className={labelCls}>Dates</span>
              <div className="flex rounded-md border border-slate-200 dark:border-slate-600 overflow-hidden h-8">
                {(['asOf', 'range'] as DateMode[]).map(m => (
                  <button key={m} type="button" onClick={() => setDateMode(m)}
                    disabled={tab === 'ageing' && m === 'range'}
                    className={cn('px-3 text-xs font-medium',
                      dateMode === m || (tab === 'ageing' && m === 'asOf')
                        ? 'bg-teal-600 text-white'
                        : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 disabled:opacity-40')}>
                    {m === 'asOf' ? 'As at' : 'Date range'}
                  </button>
                ))}
              </div>
            </div>
            {dateMode === 'asOf' || tab === 'ageing' ? (
              <div>
                <label className={labelCls} htmlFor="cd-asof">As at</label>
                <Input id="cd-asof" type="date" value={asOf} onChange={e => setAsOf(e.target.value)} />
              </div>
            ) : (
              <>
                <div>
                  <label className={labelCls} htmlFor="cd-from">From</label>
                  <Input id="cd-from" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="cd-to">To</label>
                  <Input id="cd-to" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
                </div>
              </>
            )}
            {tab === 'deposit' && (
              <Toggle label="Show fully deposited days" checked={showDeposited} onChange={setShowDeposited} />
            )}
            <Button variant="secondary" size="sm" onClick={load} disabled={!cashAccountId || loading}
                    leftIcon={<RefreshCw size={14} />}>
              Refresh
            </Button>
          </div>
        </section>

        {/* Messages */}
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 px-4 py-2 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}
        {saved && (
          <div className={cn('rounded-md border px-4 py-2 text-sm',
            saved.posted
              ? 'border-teal-200 bg-teal-50 text-teal-800 dark:bg-teal-900/20 dark:border-teal-800 dark:text-teal-200'
              : 'border-amber-200 bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200')}>
            {saved.posted
              ? <>Deposit <Link className="font-semibold underline" to={`/banking/deposits/${saved.depositId}`}>{saved.depositNumber}</Link> for {money(saved.totalAmount)} {saved.currencyCode} is saved and posted.</>
              : <>Deposit <Link className="font-semibold underline" to={`/banking/deposits/${saved.depositId}`}>{saved.depositNumber}</Link> for {money(saved.totalAmount)} {saved.currencyCode} is saved. {saved.message ?? 'It shows as pending until a supervisor confirms it on the Bank Deposits screen.'}</>}
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-slate-200 dark:border-slate-700">
          {([['deposit', 'Cash to deposit'], ['ageing', 'Unbanked cash by age']] as [Tab, string][]).map(([t, label]) => (
            <button key={t} type="button" onClick={() => setTab(t)}
              className={cn('px-4 py-2 text-sm font-medium border-b-2 -mb-px',
                tab === t
                  ? 'border-teal-600 text-teal-700 dark:text-teal-300'
                  : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400')}>
              {label}
            </button>
          ))}
        </div>

        {!cashAccountId ? (
          <p className="text-sm text-slate-500 dark:text-slate-400 py-8 text-center">
            Choose the cash in hand account to see its cash.
          </p>
        ) : loading ? (
          <div className="flex justify-center py-10"><Spinner size="lg" /></div>
        ) : tab === 'deposit' ? (
          <section className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-x-auto">
            {rows.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400 py-8 text-center">
                No cash left to deposit for these filters.
              </p>
            ) : (
              <table className="min-w-full">
                <thead className="bg-slate-50 dark:bg-slate-900/40 border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className={cn(thCls, 'text-left')}>Cost centre</th>
                    <th className={cn(thCls, 'text-left')}>Date</th>
                    <th className={cn(thCls, 'text-right')}>Net amount</th>
                    <th className={cn(thCls, 'text-right')}>Deposited</th>
                    {showPending && <th className={cn(thCls, 'text-right')} title="Saved in a deposit that is not posted yet">Pending</th>}
                    <th className={cn(thCls, 'text-right')}>Undeposited</th>
                    <th className={cn(thCls, 'text-right')}>Deposit amount ({currency})</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {rows.map(r => {
                    const key = rowKey(r);
                    const err = rowError(r);
                    const open = r.undepositedAmount > 0;
                    return (
                      <tr key={key} className={cn(!open && 'text-slate-400')}>
                        <td className={tdCls}>{r.costCenterName} <span className="text-xs text-slate-400">({r.costCenterCode})</span></td>
                        <td className={tdCls}>{dayLabel(r.businessDate)}</td>
                        <td className={cn(tdCls, 'text-right tabular-nums', r.netAmount < 0 && 'text-red-600')}>{money(r.netAmount)}</td>
                        <td className={cn(tdCls, 'text-right tabular-nums')}>{money(r.depositedAmount)}</td>
                        {showPending && <td className={cn(tdCls, 'text-right tabular-nums text-amber-700 dark:text-amber-300')}>{r.pendingAmount ? money(r.pendingAmount) : ''}</td>}
                        <td className={cn(tdCls, 'text-right tabular-nums font-medium')}>{money(r.undepositedAmount)}</td>
                        <td className={cn(tdCls, 'text-right')}>
                          {open && canCreate ? (
                            <div className="flex flex-col items-end">
                              <div className="flex items-center gap-2">
                                <input
                                  type="number" min={0} step="0.01" inputMode="decimal"
                                  aria-label={`Deposit amount for ${r.costCenterName} on ${dayLabel(r.businessDate)}`}
                                  value={amounts[key] ?? ''}
                                  onChange={e => setAmounts(a => ({ ...a, [key]: e.target.value }))}
                                  className={cn('w-32 h-8 rounded-md border px-2 text-sm text-right tabular-nums bg-white dark:bg-slate-900',
                                    err ? 'border-red-400 focus:ring-red-500' : 'border-slate-300 dark:border-slate-600 focus:ring-teal-500',
                                    'focus:outline-none focus:ring-2')}
                                />
                                <button type="button"
                                  onClick={() => setAmounts(a => ({ ...a, [key]: r.undepositedAmount.toFixed(2) }))}
                                  className="text-xs font-medium text-teal-700 hover:underline dark:text-teal-300">
                                  Deposit all
                                </button>
                              </div>
                              {err && <span className="text-xs text-red-600 mt-1">{err}</span>}
                            </div>
                          ) : (
                            <span className="text-xs">{open ? '' : 'Fully deposited'}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-slate-50 dark:bg-slate-900/40 border-t border-slate-200 dark:border-slate-700">
                  <tr className="font-semibold">
                    <td className={tdCls} colSpan={2}>Total</td>
                    <td className={cn(tdCls, 'text-right tabular-nums')}>{money(totals.net)}</td>
                    <td className={cn(tdCls, 'text-right tabular-nums')}>{money(totals.deposited)}</td>
                    {showPending && <td className={cn(tdCls, 'text-right tabular-nums')}>{money(totals.pending)}</td>}
                    <td className={cn(tdCls, 'text-right tabular-nums')}>{money(totals.undeposited)}</td>
                    <td className={cn(tdCls, 'text-right tabular-nums')}>{money(totals.entered)}</td>
                  </tr>
                </tfoot>
              </table>
            )}
          </section>
        ) : (
          <section className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-x-auto">
            {ageing.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400 py-8 text-center">
                No unbanked cash as at {asOf ? dayLabel(asOf) : 'today'}.
              </p>
            ) : (
              <table className="min-w-full">
                <thead className="bg-slate-50 dark:bg-slate-900/40 border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className={cn(thCls, 'text-left')}>Cost centre</th>
                    <th className={cn(thCls, 'text-right')}>0 to 1 days</th>
                    <th className={cn(thCls, 'text-right')}>2 to 3 days</th>
                    <th className={cn(thCls, 'text-right')}>4 to 7 days</th>
                    <th className={cn(thCls, 'text-right')}>8 days and more</th>
                    <th className={cn(thCls, 'text-right')}>Total undeposited</th>
                    <th className={cn(thCls, 'text-right')}>Pending</th>
                    <th className={cn(thCls, 'text-left')}>Oldest day</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {ageing.map(a => (
                    <tr key={a.costCenterId}>
                      <td className={tdCls}>{a.costCenterName} <span className="text-xs text-slate-400">({a.costCenterCode})</span></td>
                      <td className={cn(tdCls, 'text-right tabular-nums')}>{money(a.days0To1)}</td>
                      <td className={cn(tdCls, 'text-right tabular-nums')}>{money(a.days2To3)}</td>
                      <td className={cn(tdCls, 'text-right tabular-nums', a.days4To7 > 0 && 'text-amber-700 dark:text-amber-300')}>{money(a.days4To7)}</td>
                      <td className={cn(tdCls, 'text-right tabular-nums', a.days8Plus > 0 && 'text-red-600 font-medium')}>{money(a.days8Plus)}</td>
                      <td className={cn(tdCls, 'text-right tabular-nums font-medium')}>{money(a.totalUndeposited)}</td>
                      <td className={cn(tdCls, 'text-right tabular-nums')}>{a.pendingAmount ? money(a.pendingAmount) : ''}</td>
                      <td className={tdCls}>{a.oldestBusinessDate ? dayLabel(a.oldestBusinessDate) : ''}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50 dark:bg-slate-900/40 border-t border-slate-200 dark:border-slate-700">
                  <tr className="font-semibold">
                    <td className={tdCls}>Total ({currency})</td>
                    {(['days0To1', 'days2To3', 'days4To7', 'days8Plus', 'totalUndeposited', 'pendingAmount'] as const).map(k => (
                      <td key={k} className={cn(tdCls, 'text-right tabular-nums')}>{money(ageing.reduce((s, a) => s + a[k], 0))}</td>
                    ))}
                    <td className={tdCls} />
                  </tr>
                </tfoot>
              </table>
            )}
          </section>
        )}

        {/* Save */}
        {tab === 'deposit' && canCreate && cashAccountId && (
          <div className="flex flex-wrap items-center justify-end gap-3">
            {missing.length > 0 && (
              <span className="text-xs text-slate-500 dark:text-slate-400">Still needed: {missing.join(', ')}.</span>
            )}
            {missing.length === 0 && enteredRows.length === 0 && rows.length > 0 && (
              <span className="text-xs text-slate-500 dark:text-slate-400">Enter a deposit amount on at least one row.</span>
            )}
            <span className="text-sm text-slate-600 dark:text-slate-300">
              Depositing <strong className="tabular-nums">{money(totals.entered)}</strong> {currency}
              {costCenterId && <> for {ccLabel(costCenterId)}</>}
            </span>
            <Button onClick={handleSave} disabled={!canSave} loading={saving}>
              {saveLabel}
            </Button>
          </div>
        )}
        {tab === 'deposit' && !canConfirm && canCreate && (
          <p className="text-xs text-right text-slate-500 dark:text-slate-400">
            Your deposit is saved for a supervisor to confirm. Until then it shows as pending and that cash cannot be deposited again.
          </p>
        )}
      </div>
    </div>
  );
};
